// src/app/services/theme.service.ts
import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { ConfigService } from './config.service';
const defaultTheme = '_default';
@Injectable({
    providedIn: 'root'
})
export class ThemeService {
    private renderer: Renderer2;
    private themeLinkId = 'client-theme';
    private themeStyleId = 'client-theme-inline';

    constructor(
        private rendererFactory: RendererFactory2,
        private _configService: ConfigService,
    ) {
        this.renderer = rendererFactory.createRenderer(null, null);

    }

    public loadTheme(cliente?: string): void {
        // Verifica se existe themeScss customizado
        const customThemeScss = this._resolveThemeScss();

        if (customThemeScss) {
            // Se tem themeScss customizado, injeta inline e remove link CSS
            console.log('[ThemeService] Loading custom themeScss from branding');
            this._removeThemeLink();
            this._injectInlineTheme(customThemeScss);
        } else {
            // Comportamento padrão: carrega arquivo CSS
            const resolvedClientName = this._resolveClientName(cliente);
            this._removeInlineTheme();
            this._loadThemeFile(resolvedClientName);
        }
    }

    /**
     * Carrega arquivo CSS de tema (comportamento original)
     */
    private _loadThemeFile(clientName: string): void {
        const head = document.head;
        const existingLink = document.getElementById(this.themeLinkId);
        const themePath = `assets/themes/${clientName}.variables.css`;

        if (existingLink) {
            existingLink.setAttribute('href', themePath);
        } else {
            const link = this.renderer.createElement('link');
            link.id = this.themeLinkId;
            link.rel = 'stylesheet';
            link.href = themePath;
            this.renderer.appendChild(head, link);
        }
    }

    /**
     * Injeta CSS customizado inline via <style> tag
     */
    private _injectInlineTheme(cssContent: string): void {
        const head = document.head;
        const existingStyle = document.getElementById(this.themeStyleId);

        if (existingStyle) {
            // Atualiza conteúdo existente
            existingStyle.textContent = cssContent;
        } else {
            // Cria novo style tag
            const style = this.renderer.createElement('style');
            style.id = this.themeStyleId;
            style.type = 'text/css';
            style.textContent = cssContent;
            this.renderer.appendChild(head, style);
        }
    }

    /**
     * Remove link tag de arquivo CSS
     */
    private _removeThemeLink(): void {
        const existingLink = document.getElementById(this.themeLinkId);
        if (existingLink) {
            this.renderer.removeChild(document.head, existingLink);
        }
    }

    /**
     * Remove style tag inline
     */
    private _removeInlineTheme(): void {
        const existingStyle = document.getElementById(this.themeStyleId);
        if (existingStyle) {
            this.renderer.removeChild(document.head, existingStyle);
        }
    }

    /**
     * Resolve themeScss customizado baseado na seguinte ordem de prioridade:
     * 1. brandingData.themeScss (da API de branding)
     * 2. brandingByHost[hostname].themeScss (configuração por hostname no env.json)
     * 3. null (usa arquivo CSS padrão do cliente)
     */
    private _resolveThemeScss(): string | null {
        // 1. Prioridade máxima: branding data da API
        if (this._configService.brandingData?.themeScss) {
            return this._configService.brandingData.themeScss;
        }

        // 2. Segundo: brandingByHost (fallback para compatibilidade)
        const possibleHosts = this._configService.settings?.brandingByHost;
        const host = window.location.hostname?.toLowerCase();
        if (possibleHosts?.[host]?.themeScss) {
            return possibleHosts[host].themeScss;
        }

        // Sem themeScss customizado
        return null;
    }

    /**
     * Resolve o clientName baseado na seguinte ordem de prioridade:
     * 1. brandingData.clientName (da API de branding)
     * 2. brandingByHost[hostname].clientName (configuração por hostname no env.json)
     * 3. Parâmetro cliente passado
     * 4. settings.clientName (default do env.json)
     * 5. defaultTheme ('_default')
     */
    private _resolveClientName(cliente?: string): string {
        // 1. Prioridade máxima: branding data da API
        if (this._configService.brandingData?.clientName) {
            return this._configService.brandingData.clientName;
        }

        // 2. Segundo: brandingByHost (fallback para compatibilidade)
        const possibleHosts = this._configService.settings?.brandingByHost;
        const host = window.location.hostname?.toLowerCase();
        if (possibleHosts?.[host]?.clientName) {
            return possibleHosts[host].clientName;
        }

        // 3. Terceiro: parâmetro passado
        if (cliente && cliente.trim() !== '') {
            return cliente;
        }

        // 4. Quarto: settings.clientName (default do env.json)
        if (this._configService.settings?.clientName) {
            console.log(`[ThemeService] Using clientName from settings: ${this._configService.settings.clientName}`);
            return this._configService.settings.clientName;
        }

        // 5. Fallback final
        console.warn(`[ThemeService] No clientName found, using default: ${defaultTheme}`);
        return defaultTheme;
    }
}
// This service dynamically loads a CSS theme based on the provided client name.